import React, { useState, useEffect, useRef, useContext } from 'react'
import { gsap } from 'gsap'
import { Carousel } from 'react-responsive-carousel'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import './ProjectModal.css'
import { ThemeContext } from '../../contexts/theme'
import ImageLightbox from '../ImageLightbox/ImageLightbox'

const ProjectModal = ({ projects, initialIndex = 0, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex)
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const activeProject = projects[activeIndex]
  const modalRef = useRef(null)
  const overlayRef = useRef(null)
  const scrollRef = useRef(null)
  const projectRef = useRef(null)
  const [{ themeName }] = useContext(ThemeContext)

  const projectImages = activeProject.featuredImage
    ? [activeProject.featuredImage, ...(activeProject.images || [])]
    : activeProject.images || []

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.32 })
    gsap.fromTo(
      modalRef.current,
      { opacity: 0, scale: 0.975, y: 18 },
      { opacity: 1, scale: 1, y: 0, duration: 0.42, ease: 'power3.out' }
    )

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    if (projectRef.current) {
      gsap.fromTo(
        projectRef.current,
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.32, ease: 'power2.out', clearProps: 'all' }
      )
    }
  }, [activeIndex])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (lightboxIndex !== null) return
      if (event.key === 'Escape') handleClose()
      if (event.key === 'ArrowLeft') showProject(activeIndex - 1)
      if (event.key === 'ArrowRight') showProject(activeIndex + 1)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [activeIndex, lightboxIndex])

  const showProject = (index) => {
    const count = projects.length
    setActiveIndex((index + count) % count)
  }

  const handleClose = () => {
    gsap.to(overlayRef.current, { opacity: 0, duration: 0.24, ease: 'power2.in' })
    gsap.to(modalRef.current, {
      opacity: 0,
      scale: 0.98,
      y: 12,
      duration: 0.24,
      ease: 'power2.in',
      onComplete: onClose,
    })
  }

  const handleOverlayClick = (event) => {
    if (event.target === overlayRef.current) handleClose()
  }

  return (
    <div
      className={`pm-overlay ${themeName}`}
      ref={overlayRef}
      onClick={handleOverlayClick}
      data-lenis-prevent
      data-lenis-prevent-wheel
      data-lenis-prevent-touch
    >
      <div className="pm-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label={`${activeProject.title} project details`}>
        <header className="pm-toolbar">
          <button
            className="pm-project-nav"
            type="button"
            onClick={() => showProject(activeIndex - 1)}
            aria-label="Previous project"
          >
            <span aria-hidden="true">←</span> Previous
          </button>
          <span className="pm-project-count">{activeIndex + 1} / {projects.length}</span>
          <button
            className="pm-project-nav"
            type="button"
            onClick={() => showProject(activeIndex + 1)}
            aria-label="Next project"
          >
            Next <span aria-hidden="true">→</span>
          </button>
          <button className="pm-close" type="button" onClick={handleClose} aria-label="Close project details">
            <span aria-hidden="true">&times;</span>
          </button>
        </header>

        <div className="pm-scroll" ref={scrollRef}>
          <main className="pm-project" ref={projectRef} key={activeProject.title}>
            {projectImages.length > 0 && (
              <div className="pm-carousel-wrap">
                <Carousel
                  key={activeProject.title}
                  showArrows={projectImages.length + (activeProject.demoLink ? 1 : 0) > 1}
                  showThumbs={false}
                  showStatus={false}
                  infiniteLoop={projectImages.length + (activeProject.demoLink ? 1 : 0) > 1}
                  autoPlay={false}
                  swipeable
                  emulateTouch
                  useKeyboardArrows
                >
                  {projectImages.map((image, index) => (
                    <div className="pm-carousel-slide" key={`${image}-${index}`}>
                      <button
                        className="pm-image-trigger"
                        type="button"
                        onClick={() => setLightboxIndex(index)}
                        aria-label={`View ${activeProject.title} image ${index + 1} full screen`}
                      >
                        <img
                          src={image}
                          alt={activeProject.featuredImage && index === 0
                            ? `${activeProject.title} project cover`
                            : `${activeProject.title} project image ${index + 1}`}
                          loading={index === 0 ? 'eager' : 'lazy'}
                        />
                      </button>
                    </div>
                  ))}
                  {activeProject.demoLink && (
                    <div className="pm-carousel-slide pm-carousel-slide--video" key={`${activeProject.title}-demo-video`}>
                      <iframe
                        src="https://www.youtube-nocookie.com/embed/MwNOuVCunjQ?rel=0"
                        title={`${activeProject.title} demo video`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                  )}
                </Carousel>
              </div>
            )}

            <div className="pm-details">
              <div className="pm-details__intro">
                <p className="pm-eyebrow">Project case study</p>
                <h2 className="pm-details__title">{activeProject.title}</h2>
                <p className="pm-details__description">{activeProject.description}</p>
              </div>

              {activeProject.caseStudy?.length > 0 && (
                <div className="pm-case-study">
                  {activeProject.caseStudy
                    .filter((section) => section.title.toLowerCase() !== 'overview')
                    .map((section) => (
                      <section className="pm-case-study__section" key={section.title}>
                        <h3>{section.title}</h3>
                        <p>{section.body}</p>
                      </section>
                    ))}
                </div>
              )}

              <div className="pm-actions">
                {activeProject.liveLink && (
                  <a href={activeProject.liveLink} target="_blank" rel="noopener noreferrer" className="pm-details__link">
                    View live project <span aria-hidden="true">↗</span>
                  </a>
                )}
                {activeProject.demoLink && (
                  <a href={activeProject.demoLink} target="_blank" rel="noopener noreferrer" className="pm-secondary-link">Demo video</a>
                )}
                {activeProject.repoLink && (
                  <a href={activeProject.repoLink} target="_blank" rel="noopener noreferrer" className="pm-secondary-link">GitHub repository</a>
                )}
                {activeProject.reportLink && (
                  <a href={activeProject.reportLink} target="_blank" rel="noopener noreferrer" className="pm-secondary-link">GSoC report</a>
                )}
                {activeProject.gsocLink && (
                  <a href={activeProject.gsocLink} target="_blank" rel="noopener noreferrer" className="pm-secondary-link">Google Summer of Code</a>
                )}
                {activeProject.appStoreLink && (
                  <a href={activeProject.appStoreLink} target="_blank" rel="noopener noreferrer" className="pm-secondary-link">App Store</a>
                )}
                {activeProject.playStoreLink && (
                  <a href={activeProject.playStoreLink} target="_blank" rel="noopener noreferrer" className="pm-secondary-link">Google Play</a>
                )}
                {activeProject.slug && (
                  <a href={`/projects/${activeProject.slug}`} className="pm-secondary-link">Full project page</a>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      {lightboxIndex !== null && (
        <ImageLightbox
          images={projectImages}
          initialIndex={lightboxIndex}
          title={activeProject.title}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  )
}

export default ProjectModal
